import type { BillingTranslations } from "../../types/billing";

export const billing: BillingTranslations = {
  plans: {
    storageRequested: "{requested} requested; {available} available.",
    legacyAgreement:
      "This agreed rate applies to buildings on legacy organisation pricing. Buildings with their own plan use that plan's price.",
    currentLegacyRate:
      "Current legacy building rate: {amount} per month. Confirm a plan to switch this building to managed plan billing.",
    summary: {
      included: "WHAT'S INCLUDED",
      chatQuestions: "Chat questions / month",
      currentRate: "Current rate:",
      perBuildingMonth: "/ building / month",
      perMonth: "/ month",
      excludingVat: "excl. VAT",
      monthlyTotal: "Adds to your monthly total",
    },
    activationPending:
      "Building activation is awaiting billing confirmation. Projects, chat questions, PM issues and uploads are unavailable until it is confirmed. Complete any required payment action in Billing → Invoices before retrying activation.",
    retryActivation: "Retry activation",
    activated: "Building activated.",
    names: { free: "Free", starter: "Starter", growing: "Growing", scale: "Scale" },
    plan: "Plan",
    monthlyPrice: "{amount}/month",
    limitsInfo: "Plan limits",
    storage: "Building storage",
    projects: "Non-archived projects",
    chatQuestions: "Chat questions per calendar month",
    pmIssues: "PM issues",
    unlimited: "Unlimited",
    allowance: "{count}",
    usage: "{used} / {count}",
    freeLimit: "One free building per organisation. Archiving does not free up a slot.",
    noCard: "No payment card or billing details needed for Free.",
    change: "Change plan",
    confirm: "Confirm plan",
    changed: "Building plan changed.",
    changeFailed: "Could not change the building plan.",
    changeTiming:
      "Limits change after the plan is confirmed. The new monthly price starts at the next renewal, no charge or credit for the current billing cycle.",
    pending: "Changing to {plan}",
    pendingHelp:
      "The plan change is awaiting confirmation. The current plan is displayed until confirmation.",
    unavailable: "The building plans could not be loaded.",
    ownerCreation: "Ask the organisation owner to choose a plan and add this building in Billing.",
    freeBuildingLimit:
      "This organisation already has its Free building. Upgrade that building or choose a paid plan for the new building.",
    freeAccountLimit:
      "Your account already has a Free building. Upgrade it before adding another Free building, or choose a paid plan.",
    projectLimit:
      "This building's plan allows {limit} non-archived project(s). Archive a project or ask the organisation owner to upgrade the building's plan.",
    pmIssueLimit:
      "This building's plan allows {limit} PM issues. Remove an issue or ask the organisation owner to upgrade the building's plan.",
    chatLimit:
      "This building's plan allows {limit} chat questions per calendar month. Try again next month or ask the organisation owner to upgrade the building's plan.",
    chatUnavailable:
      "The building's chat allowance could not be checked. No question was sent. Please try again.",
    storageLimit:
      "This building has {limit} of storage. Remove files or ask the organisation owner to upgrade the building's plan.",
    planLimit:
      "The building exceeds the selected plan's allowance. Reduce its usage or choose a plan with a higher allowance.",
    planStorageLimit:
      "The building exceeds the selected plan's storage allowance. Remove files or choose a plan with more storage, then try again.",
    planStorageAllowance:
      "The selected plan allows {limit} of storage. Remove files or choose a plan with more storage, then try again.",
    planChangeBusy:
      "A plan change is already pending for this building. Wait for confirmation before submitting another change.",
  },
  nav: {
    group: "Billing",
    overview: "Overview",
    details: "Billing details",
    invoices: "Invoices",
  },
  page: {
    overviewTitle: "Overview",
    overviewSubtitle: "Invoices are issued from this list",
    detailsTitle: "Billing details",
    detailsSubtitle: "Used on every invoice. Only the organisation owner can change these.",
    invoicesTitle: "Invoices",
    pilotTitle: "Not active",
    pilotBody:
      "This organisation is on a pilot, so nothing is being charged and there is nothing to see here yet. When the pilot ends we will agree a rate with you, and this is where your buildings and invoices will appear.",
  },
  shell: {
    statusUnknownTitle: "Billing status unavailable",
    statusUnknownBody:
      "We cannot verify this organisation's billing status right now, so no new buildings can be added. Everything else keeps working as usual.",
    reasonReadOnly:
      "This organisation is read only until the overdue invoice is settled, so this action is unavailable.",
    reasonStatusUnknown:
      "This action is unavailable while this organisation's billing status cannot be verified.",
    reasonDetailsMissing: "Add your billing details before adding a building.",
    reasonPaymentMethodMissing: "Set up your payment method before adding a building.",
  },
  banners: {
    pilotTitle: "Pilot account",
    pilotBody:
      "This organisation is on a pilot until {date}. No charges apply and no billing information is required.",
    pilotBodyOpenEnded:
      "This organisation is on a pilot. No charges apply and no billing information is required until an end date is agreed.",
    detailsMissingTitle: "Add your billing details",
    detailsMissingBody:
      "We need your company data before we can invoice you. You can add your first building once these are saved.",
    detailsMissingAction: "Add details",
    overdueTitle: "Invoice {number} is overdue",
    overdueBody:
      "Due on {date}. If it stays unpaid for 30 days, this organisation moves to read only.",
    overdueBodyNoDueDate:
      "This invoice is still unpaid. If it stays unpaid for 30 days from the invoice date, this organisation moves to read only.",
    readOnlyTitle: "This organisation is read only",
    readOnlyBody:
      "An invoice has been unpaid for more than 30 days. Everything stays visible and downloadable. You cannot add buildings, open projects or upload documents until the invoice is settled.",
    viewInvoice: "View invoice",
    paymentMethodMissingTitle: "Add a payment method",
    paymentMethodMissingBody:
      "Choose card or SEPA on the Billing details page and complete the setup before adding buildings.",
    setUpPaymentMethod: "Set up payment method",
    paymentFailedTitle: "Payment failed for invoice {number}",
    paymentFailedBody:
      "We could not charge your card on {date}. Update your payment method or the organisation moves to read only 30 days after the invoice date.",
    updatePaymentMethod: "Update payment method",
  },
  rate: {
    agreedRate: "Your agreed rate",
    agreedUntil: "Agreed until",
    openEnded: "No end date",
    contact: "Your contact",
    perBuildingMonth: "/ building / month",
  },
  summary: {
    currentPeriod: "Current period",
    closesIn_one: "Closes in {days} day",
    closesIn_other: "Closes in {days} days",
    buildings: "Billed buildings",
    addedThisPeriod_one: "{count} added this period",
    addedThisPeriod_other: "{count} added this period",
    nextInvoiceOn: "Next invoice · {date}",
    renewalOn: "Monthly renewal · {date}",
    excludingVat: "excluding VAT",
    taxPerAgreement: "VAT follows the existing agreement",
    chargedTo: "charged to {method}",
    sentByEmail: "sent by email",
    sentAsEInvoice: "sent as an e-invoice",
  },
  table: {
    sectionTitle: "Buildings this period",
    building: "Building",
    servicePeriod: "Service period",
    amount: "Amount",
    action: "Action",
    footerTotal: "Total",
    emptyTitle: "No buildings yet",
    emptyBody:
      "Choose a plan when you add your first building. Free does not require a payment card.",
    removalScheduled: "Removal scheduled",
    remove: "Remove",
    add: "Add building",
  },
  removed: {
    sectionTitle: "Removed buildings",
    building: "Building",
    usableUntil: "Usable until",
    deletedOn: "Deleted on",
    removedOn: "Removed {date}",
    restore: "Restore",
    restored: "Building restored.",
    restoreFailed: "Could not restore the building.",
  },
  transfer: {
    before:
      "Changing owner or manager? A building and all of its data can be transferred to another organisation instead of removed. Write to",
    after: "and our team will handle it.",
  },
  consent: {
    addTitle: "Add a building",
    restoreTitle: "Restore this building",
    description: "A building is what your organisation is charged for, so this is what it costs.",
    quoteLead: "This adds to your monthly total",
    quoteDetail:
      "{amount} plus applicable VAT is charged and invoiced now for the remaining {days} days of your billing cycle. On your next billing date, all buildings renew together with one charge and invoice.",
    quoteFullMonth:
      "Full monthly price of {amount} plus applicable VAT charged and invoiced now. Today becomes your organisation’s monthly billing date. All buildings renew together on that date.",
    quoteNextInvoice:
      "{amount} plus applicable VAT for the remaining {days} days will be included in your organisation’s next monthly invoice on the 1st, together with the next month’s subscription. No payment is taken now.",
    quoteTimingUnknown:
      "The amount due is confirmed at activation. New paid buildings are charged and invoiced immediately; renewals are combined on your organisation’s billing date.",
    restoreSamePeriodLead: "This period is already paid for",
    restoreSamePeriod: "No additional charge this period.",
    loading: "Working out what this costs…",
    quoteUnavailable: "The price could not be loaded, so nothing has been added yet.",
    retry: "Try again",
    cancel: "Cancel",
    agreeAdd: "Agree and add building",
    agreeRestore: "Agree and restore building",
  },
  addDialog: {
    title: "Add building",
    address: "Address",
    addressPlaceholder: "Hoone F, Tallinn",
    cadastralCode: "Cadastral code",
    cadastralPlaceholder: "12345:678:9012",
    cadastralFormat: "Use the format 12345:678:9012.",
    cancel: "Cancel",
    submit: "Add building",
    added: "Building added to this billing period.",
    addFailed: "Could not add the building.",
  },
  removeDialog: {
    title: "Remove building",
    activeUntil: "active until {date}",
    body: "The building stays fully usable until the end of the period you have paid for. From {date} it is no longer charged and your monthly total drops by {amount}.",
    bodyWithoutAmount:
      "The building stays fully usable until the end of the period you have paid for. From {date} it is no longer charged.",
    afterHeading: "After {date}",
    afterBody:
      "The building can no longer be used and nothing new can be uploaded. You can download its documents, and you can restore the building at any point in the following 30 days. On {date} it is deleted and cannot be recovered.",
    datesNote:
      "These dates are confirmed against your billing period once you remove the building.",
    transferHint:
      "If the building is changing owner or manager, hand it over to their organisation instead. Everything stays in place.",
    cancel: "Cancel",
    confirm: "Remove building",
    removed: "Building scheduled for removal.",
    removeFailed: "Could not remove the building.",
  },
  details: {
    draftSaveFailed:
      "We could not retain your building details. Please try again before leaving this form.",
    returnToBuilding: "Continue adding building",
    saveAndSetUp: "Save and set up payment",
    companyName: "Company name",
    optional: "Optional",
    registryCode: "Registry code",
    vatNumber: "VAT number",
    vatPlaceholder: "EE100000000",
    legalAddress: "Legal address",
    invoiceEmail: "Invoice email",
    invoiceEmailPlaceholder: "arved@naidis.ee",
    purchaseOrderNumber: "Purchase order number",
    purchaseOrderPlaceholder: "Optional",
    purchaseOrderHint: "Shown on every invoice if your accounting needs it.",
    paymentMethod: "Payment method",
    paymentByCard: "Card (automatic)",
    paymentBySepa: "SEPA direct debit (automatic)",
    paymentByEmail: "Bank transfer by email",
    paymentByEInvoice: "Bank transfer e-invoice",
    recommended: "— recommended",
    paymentHint: "Invoices are issued with a 14 day term.",
    paymentHintAutomatic: "Each invoice is charged automatically on the day it is issued.",
    noPaymentMethod: "No payment method yet",
    saveMethodFirst: "Save this payment method first, then set it up.",
    setUpPaymentMethod: "Set up payment method",
    changePaymentMethod: "Change",
    setupFailed: "Could not open the payment method setup.",
    setupSaved: "Payment method saved.",
    setupCancelled: "Payment method setup was cancelled.",
    save: "Save details",
    saved: "Billing details saved.",
    saveFailed: "Could not save billing details.",
    restrictedTitle: "Read-only billing details",
    ownerOnly: "Only the organisation owner can edit billing details.",
    errors: {
      companyRequired: "Company name is required.",
      registryTooLong: "Registry code must be at most {max} characters.",
      vatCountryFormat: "The VAT number for {country} must look like {example}.",
      vatPrefix:
        "Start the VAT number with an EU VAT country prefix. Greece is EL and Northern Ireland is XI.",
      addressRequired: "Legal address is required.",
      emailInvalid: "Enter a valid invoice email.",
      companyTooLong: "Company name must be {max} characters or fewer.",
      addressTooLong: "Legal address must be {max} characters or fewer.",
      emailTooLong: "Invoice email must be {max} characters or fewer.",
      purchaseOrderTooLong: "Purchase order number must be {max} characters or fewer.",
    },
  },
  invoices: {
    invoice: "Invoice",
    period: "Period",
    due: "Due",
    status: "Status",
    amount: "Amount",
    pdf: "PDF",
    dueOnIssue: "Charged on issue",
    buildingCount_one: "{count} building",
    buildingCount_other: "{count} buildings",
    statusDraft: "Draft",
    statusDue: "Due",
    statusOverdue: "Overdue",
    statusPaid: "Paid",
    statusVoid: "Void",
    statusUncollectible: "Written off",
    statusPaymentFailed: "Payment failed",
    paidOn: "Paid on {date}",
    download: "Download",
    downloadLabel: "Download {number} PDF",
    downloadUnavailable: "The invoice PDF is not available yet.",
    downloadPending: "The invoice PDF download is being prepared.",
    downloadFailed: "Could not download the invoice.",
    emptyTitle: "No invoices yet",
    emptyBody: "Issued invoices and their PDF downloads will appear here.",
  },
  restrictions: {
    addReadOnly: "Adding buildings is blocked while the organisation is read-only.",
    addDetailsMissing: "Add the required billing details before adding a building.",
    addPaymentMethodMissing: "Set up your payment method before adding a building.",
    restoreReadOnly: "Restoring buildings is blocked while the organisation is read-only.",
    restoreDetailsMissing: "Add the required billing details before restoring a building.",
    restorePaymentMethodMissing: "Set up your payment method before restoring a building.",
    restoreExpired: "The 30-day recovery window has expired.",
    restoring: "This building is being restored.",
    restorePending: "Wait for the current building restoration to finish.",
    ownerOnly: "Only the organisation owner can change buildings from the billing pages.",
    noPermission: "You do not have permission to do this.",
  },
  guards: {
    readOnly:
      "This organisation is read only until its overdue invoice is settled, so nothing new can be created.",
    detailsMissing: "Save your billing details before adding a building.",
    buildingLapsed:
      "This building was removed and is past the date it stopped being usable. Restore it on the billing overview to add to it again.",
  },
  errors: {
    contextTitle: "Billing details could not be loaded.",
    contextBody: "No billing changes are available while status is unknown.",
    overviewTitle: "Billing overview could not be loaded.",
    overviewBody: "No billing changes are available while status is unknown.",
    invoicesTitle: "Invoices could not be loaded.",
    retry: "Try again",
    notOwner: "Only the organisation owner can do this.",
    readOnly: "This organisation is read-only until the overdue invoice is settled.",
    detailsMissing: "Save your billing details first.",
    paymentMethodMissing: "Set up a card or SEPA payment method first.",
    pilot: "This organisation is on a pilot, so there is nothing to bill yet.",
    validation: "Some of the details are not valid. Check the form and try again.",
    validationDetail: "Some of the details are not valid: {message}",
    stripe: "Billing is temporarily unavailable. Please try again in a moment.",
    stripeDetail: "Billing request failed: {message}",
    generic: "Something went wrong. Please try again.",
  },
};
